import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { FileText, Download, Search as SearchIcon, Filter, SortAsc, Trash2, FileCheck, FileX, Eye, Edit, X, FileImage, File, Ban, XCircle, Loader2 } from 'lucide-react';
import { apiRequest, authService } from '../utils/auth';
import { toast } from 'sonner';

const STATUS_OPTIONS = ['Pending', 'Ongoing', 'Completed', 'PO Accepted', 'RFQ Rejected', 'Denied'];

export default function Reports() {
  const [searchParams] = useSearchParams();
  const urlStatus = searchParams.get('status');
  
  const [rfqs, setRfqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [statusFilter, setStatusFilter] = useState(urlStatus && STATUS_OPTIONS.includes(urlStatus) ? urlStatus : '');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewRFQ, setViewRFQ] = useState(null);
  const [editRFQ, setEditRFQ] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [denyModal, setDenyModal] = useState({ open: false, rfqId: null, rfqName: '' });
  const [denyReason, setDenyReason] = useState('');
  const pageSize = 25;
  const isAdmin = authService.isAdmin();

  const categoryOptions = ['Forging', 'Machining', 'Welding', 'Silver Plating', 'Assembly', 'Heat Treatment', 'Painting', 'Anodizing', 'Electroplating', 'Powder Coating'];

  useEffect(() => {
    fetchRFQs();
  }, [sortBy, sortOrder, statusFilter, page]);

  const fetchRFQs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (sortBy) params.append('sort_by', sortBy);
      if (sortOrder) params.append('sort_order', sortOrder);
      if (statusFilter) params.append('status', statusFilter);
      params.append('skip', ((page - 1) * pageSize).toString());
      params.append('limit', pageSize.toString());
      
      const data = await apiRequest(`/api/rfq?${params.toString()}`);
      setRfqs(data.rfqs || []);
      setTotalCount(data.total || data.rfqs?.length || 0);
    } catch (error) {
      toast.error('Failed to fetch RFQs');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (rfqId) => {
    if (!window.confirm('Delete this RFQ?')) return;

    try {
      await apiRequest(`/api/rfq/${rfqId}`, { method: 'DELETE' });
      toast.success('RFQ deleted successfully');
      fetchRFQs();
    } catch (error) {
      toast.error(error.message || 'Failed to delete RFQ');
    }
  };

  const openEditModal = (rfq) => {
    setEditRFQ(rfq);
    setEditFormData({
      rfq_id: rfq.rfq_id || '',
      customer_code: rfq.customer_code || '',
      part_no: rfq.part_no || '',
      part_name: rfq.part_name || '',
      part_description: rfq.part_description || '',
      rfq_qty: rfq.rfq_qty || '',
      category: rfq.category || [],
      rfq_date: rfq.rfq_date || '',
      quotation_date: rfq.quotation_date || '',
      quoted_price: rfq.quoted_price || '',
      final_price: rfq.final_price || '',
      purchase_order_number: rfq.purchase_order_number || '',
      po_qty: rfq.po_qty || '',
      po_value: rfq.po_value || '',
      buyer_name: rfq.buyer_name || '',
      status: rfq.status || 'Pending',
      // Load parts array for multi-part RFQs
      parts: rfq.parts && rfq.parts.length > 0 ? rfq.parts.map(p => ({...p})) : null,
    });
  };

  const handleRejectPO = async (rfqId) => {
    const reason = window.prompt('Enter rejection reason (optional):');
    if (reason === null) return; // User cancelled
    
    try {
      const params = reason ? `?reason=${encodeURIComponent(reason)}` : '';
      await apiRequest(`/api/rfq/${rfqId}/reject-po${params}`, { method: 'PUT' });
      toast.success('PO rejected successfully');
      fetchRFQs();
    } catch (error) {
      toast.error(error.message || 'Failed to reject PO');
    }
  };

  const handleEditSave = async () => {
    if (!editRFQ) return;
    setSaving(true);
    try {
      const updateData = {
        ...editFormData,
        rfq_qty: parseInt(editFormData.rfq_qty) || 0,
        quoted_price: editFormData.quoted_price ? parseFloat(editFormData.quoted_price) : null,
        final_price: editFormData.final_price ? parseFloat(editFormData.final_price) : null,
        po_qty: editFormData.po_qty ? parseInt(editFormData.po_qty) : null,
        po_value: editFormData.po_value ? parseFloat(editFormData.po_value) : null,
      };
      
      // Handle multi-part updates
      if (editFormData.parts && editFormData.parts.length > 0) {
        updateData.parts = editFormData.parts.map(p => ({
          ...p,
          rfq_qty: parseInt(p.rfq_qty) || 0,
          quoted_price: p.quoted_price ? parseFloat(p.quoted_price) : null,
        }));
        // Recalculate total qty from parts
        updateData.rfq_qty = updateData.parts.reduce((sum, p) => sum + (parseInt(p.rfq_qty) || 0), 0);
      }
      
      await apiRequest(`/api/rfq/${editRFQ._id}`, {
        method: 'PUT',
        body: JSON.stringify(updateData),
      });
      toast.success('RFQ updated successfully');
      setEditRFQ(null);
      fetchRFQs();
    } catch (error) {
      toast.error(error.message || 'Failed to update RFQ');
    } finally {
      setSaving(false);
    }
  };

  const openDenyModal = (rfqId, rfqName) => {
    setDenyModal({ open: true, rfqId, rfqName });
    setDenyReason('');
  };

  const handleDenyRFQ = async () => {
    if (!denyModal.rfqId) return;
    
    try {
      const url = denyReason 
        ? `/api/rfq/${denyModal.rfqId}/deny?reason=${encodeURIComponent(denyReason)}`
        : `/api/rfq/${denyModal.rfqId}/deny`;
      await apiRequest(url, { method: 'PUT' });
      toast.success('RFQ denied successfully');
      setDenyModal({ open: false, rfqId: null, rfqName: '' });
      setDenyReason('');
      fetchRFQs();
    } catch (error) {
      toast.error(error.message || 'Failed to deny RFQ');
    }
  };

  const handleCategoryToggle = (cat) => {
    setEditFormData((prev) => ({
      ...prev,
      category: prev.category.includes(cat)
        ? prev.category.filter((c) => c !== cat)
        : [...prev.category, cat],
    }));
  };

  const openFilePreview = (fileName, fileUrl, fileType) => {
    setPreviewFile({ name: fileName, url: fileUrl, type: fileType });
  };

  const getFileIcon = (fileName) => {
    const ext = fileName?.toLowerCase().split('.').pop();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
      return <FileImage className="w-6 h-6 text-green-600" />;
    }
    return <File className="w-6 h-6 text-blue-600" />;
  };

  const isImageFile = (fileName) => {
    const ext = fileName?.toLowerCase().split('.').pop();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
  };

  const isPdfFile = (fileName) => {
    return fileName?.toLowerCase().endsWith('.pdf');
  };

  const isExcelFile = (fileName) => {
    const ext = fileName?.toLowerCase().split('.').pop();
    return ['xlsx', 'xls', 'csv'].includes(ext);
  };

  const filteredRFQs = rfqs.filter((rfq) => {
    const search = searchTerm.toLowerCase();
    return (
      rfq.customer_code?.toLowerCase().includes(search) ||
      rfq.part_name?.toLowerCase().includes(search) ||
      rfq.part_no?.toLowerCase().includes(search)
    );
  });

  const getStatus = (rfq) => {
    return rfq.status || 'Pending';
  };

  const statusColors = {
    Pending: 'bg-yellow-100 text-yellow-800',
    Ongoing: 'bg-blue-100 text-blue-800',
    Completed: 'bg-green-100 text-green-800',
    'PO Accepted': 'bg-emerald-100 text-emerald-800',
    'RFQ Rejected': 'bg-red-100 text-red-800',
    Denied: 'bg-gray-100 text-gray-800',
  };

  return (
    <Layout>
      <div data-testid="reports-page">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-[#0A2540]">Reports</h2>
            <p className="text-sm text-gray-600 mt-1">View and manage RFQ records</p>
          </div>
        </div>

        <Card className="p-6 border-gray-200 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* SEARCH */}
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <SearchIcon className="w-4 h-4" />
                SEARCH
              </Label>
              <Input
                data-testid="search-input"
                placeholder="Search by code, name, part, buyer, PO..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* SORT */}
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <SortAsc className="w-4 h-4" />
                SORT BY STATUS
              </Label>
              <Select
                value={statusFilter}
                onValueChange={(val) => setStatusFilter(val === 'all' ? '' : val)}
              >
                <SelectTrigger data-testid="sort-status">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="Pending">Pending</SelectItem>
                  <SelectItem value="Ongoing">Ongoing</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                  <SelectItem value="PO Accepted">PO Accepted</SelectItem>
                  <SelectItem value="RFQ Rejected">RFQ Rejected</SelectItem>
                  <SelectItem value="Denied">Denied</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* FILTER */}
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <Filter className="w-4 h-4" />
                SORT BY DATE
              </Label>
              <Select
                value={`${sortBy}-${sortOrder}`}
                onValueChange={(val) => {
                  const [by, order] = val.split('-');
                  setSortBy(by);
                  setSortOrder(order);
                }}
              >
                <SelectTrigger data-testid="sort-date">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="created_at-desc">Newest First</SelectItem>
                  <SelectItem value="created_at-asc">Oldest First</SelectItem>
                  <SelectItem value="rfq_date-desc">RFQ Date (Newest)</SelectItem>
                  <SelectItem value="rfq_date-asc">RFQ Date (Oldest)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        <Card className="p-6 border-gray-200">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600 mb-4" />
              <p className="text-gray-500">Loading RFQs...</p>
            </div>
          ) : filteredRFQs.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No RFQs available</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>RFQ ID</TableHead>
                    <TableHead>Customer Code</TableHead>
                    <TableHead>Part No.</TableHead>
                    <TableHead>Part Name</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Quantity</TableHead>
                    <TableHead>Quoted Price</TableHead>
                    <TableHead>Final Price</TableHead>
                    <TableHead>Est. Business</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Drawing</TableHead>
                    <TableHead>Quotation</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRFQs.map((rfq, index) => {
                    const status = getStatus(rfq);
                    
                    return (
                      <TableRow key={index} data-testid={`rfq-row-${index}`}>
                        <TableCell className="font-medium">{rfq.rfq_id}</TableCell>
                        <TableCell>{rfq.customer_code}</TableCell>
                        <TableCell>{rfq.part_no}</TableCell>
                        <TableCell>{rfq.part_name}</TableCell>
                        <TableCell>
                          <div className="text-xs">
                            {Array.isArray(rfq.category) ? rfq.category.join(', ') : rfq.category}
                          </div>
                        </TableCell>
                        <TableCell>{rfq.rfq_qty?.toLocaleString('en-IN')}</TableCell>
                        <TableCell>
                          {rfq.quoted_price ? `₹${parseFloat(rfq.quoted_price).toLocaleString('en-IN')}` : '-'}
                        </TableCell>
                        <TableCell>
                          {rfq.final_price ? `₹${parseFloat(rfq.final_price).toLocaleString('en-IN')}` : '-'}
                        </TableCell>
                        <TableCell>₹{rfq.estimated_business?.toLocaleString('en-IN')}</TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusColors[status]}`}>
                            {status}
                          </span>
                        </TableCell>
                        <TableCell>
                          {(() => {
                            // Check top-level first, then parts array
                            const drawingName = rfq.drawing_file_name || (rfq.parts && rfq.parts[0]?.drawing_file_name);
                            const drawingUrl = rfq.drawing_url || (rfq.parts && rfq.parts[0]?.drawing_url);
                            if (drawingName && drawingUrl) {
                              return (
                                <button
                                  onClick={() => openFilePreview(drawingName, drawingUrl, 'drawing')}
                                  className="flex items-center gap-1 text-green-600 hover:text-green-700 hover:underline"
                                  data-testid={`drawing-link-${index}`}
                                >
                                  <FileCheck className="w-4 h-4" />
                                  <span className="text-xs">{drawingName.substring(0, 15)}...</span>
                                </button>
                              );
                            }
                            return (
                              <div className="flex items-center gap-1 text-gray-400">
                                <FileX className="w-4 h-4" />
                                <span className="text-xs">No file</span>
                              </div>
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          {rfq.quotation_file_name && rfq.quotation_url ? (
                            <button
                              onClick={() => openFilePreview(rfq.quotation_file_name, rfq.quotation_url, 'quotation')}
                              className="flex items-center gap-1 text-blue-600 hover:text-blue-700 hover:underline"
                              data-testid={`quotation-link-${index}`}
                            >
                              <FileCheck className="w-4 h-4" />
                              <span className="text-xs">{rfq.quotation_file_name.substring(0, 15)}...</span>
                            </button>
                          ) : (
                            <div className="flex items-center gap-1 text-gray-400">
                              <FileX className="w-4 h-4" />
                              <span className="text-xs">No file</span>
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setViewRFQ(rfq)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              data-testid={`view-rfq-${index}`}
                              title="View RFQ"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditModal(rfq)}
                              className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                              data-testid={`edit-rfq-${index}`}
                              title="Edit RFQ"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            {rfq.status !== 'Denied' && rfq.status !== 'RFQ Rejected' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openDenyModal(rfq._id, rfq.rfq_id)}
                                className="text-gray-600 hover:text-gray-700 hover:bg-gray-50"
                                data-testid={`deny-rfq-${index}`}
                                title="Deny RFQ"
                              >
                                <Ban className="w-4 h-4" />
                              </Button>
                            )}
                            {rfq.status !== 'RFQ Rejected' && rfq.status !== 'Denied' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRejectPO(rfq._id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`reject-rfq-${index}`}
                                title="Reject RFQ"
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            )}
                            {isAdmin && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(rfq._id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-rfq-${index}`}
                                title="Delete RFQ"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          <div className="flex items-center justify-between mt-4 px-2">
            <p className="text-sm text-gray-600">
              Showing {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, totalCount)} of {totalCount} RFQs
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </Button>
              <span className="text-sm px-3">Page {page}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => p + 1)}
                disabled={page * pageSize >= totalCount}
              >
                Next
              </Button>
            </div>
          </div>

          {filteredRFQs.length > 0 && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">Total RFQs</p>
                  <p className="text-2xl font-bold text-[#0A2540]">{filteredRFQs.length}</p>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">Total Quantity</p>
                  <p className="text-2xl font-bold text-[#0A2540]">
                    {filteredRFQs.reduce((sum, rfq) => sum + parseInt(rfq.rfq_qty || 0), 0).toLocaleString('en-IN')}
                  </p>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">Total Value</p>
                  <p className="text-2xl font-bold text-[#0A2540]">
                    ₹{filteredRFQs.reduce((sum, rfq) => sum + (rfq.estimated_business || 0), 0).toLocaleString('en-IN')}
                  </p>
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* View RFQ Modal */}
      {viewRFQ && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-semibold text-[#0A2540]">RFQ Details</h2>
                <button
                  onClick={() => setViewRFQ(null)}
                  className="text-gray-500 hover:text-gray-700"
                  data-testid="close-view-modal"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Header Info */}
                <div className="grid grid-cols-2 gap-4 pb-4 border-b">
                  <div>
                    <Label className="text-gray-600">RFQ ID</Label>
                    <p className="font-semibold text-lg">{viewRFQ.rfq_id}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Customer Code</Label>
                    <p className="font-semibold text-lg">{viewRFQ.customer_code}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">RFQ Date</Label>
                    <p className="font-medium">{viewRFQ.rfq_date}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Buyer Name</Label>
                    <p className="font-medium">{viewRFQ.buyer_name || '-'}</p>
                  </div>
                </div>

                {/* Parts Section */}
                <div>
                  <Label className="text-gray-600 text-lg mb-3 block">
                    Parts ({viewRFQ.parts?.length || 1})
                  </Label>
                  
                  {viewRFQ.parts && viewRFQ.parts.length > 0 ? (
                    <div className="space-y-4">
                      {viewRFQ.parts.map((part, idx) => (
                        <div key={idx} className="p-4 bg-gray-50 rounded-lg border">
                          <div className="flex items-center justify-between mb-3">
                            <span className="font-semibold text-[#0A2540]">Part {idx + 1}</span>
                            {part.category && part.category.length > 0 && (
                              <div className="flex gap-1">
                                {(Array.isArray(part.category) ? part.category : [part.category]).map((cat, i) => (
                                  <span key={i} className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs">
                                    {cat}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <span className="text-gray-500">Part No:</span>
                              <span className="ml-2 font-medium">{part.part_no}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Part Name:</span>
                              <span className="ml-2 font-medium">{part.part_name}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Quantity:</span>
                              <span className="ml-2 font-medium">{part.rfq_qty?.toLocaleString('en-IN')}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Quoted Price:</span>
                              <span className="ml-2 font-medium">{part.quoted_price ? `₹${parseFloat(part.quoted_price).toLocaleString('en-IN')}` : '-'}</span>
                            </div>
                            {part.part_description && (
                              <div className="col-span-2">
                                <span className="text-gray-500">Description:</span>
                                <span className="ml-2">{part.part_description}</span>
                              </div>
                            )}
                            {part.drawing_file_name && (
                              <div className="col-span-2">
                                <span className="text-gray-500">Drawing:</span>
                                <span className="ml-2 text-blue-600">{part.drawing_file_name}</span>
                              </div>
                            )}
                            {part.estimated_business > 0 && (
                              <div className="col-span-2">
                                <span className="text-gray-500">Part Value:</span>
                                <span className="ml-2 font-semibold text-green-700">₹{part.estimated_business?.toLocaleString('en-IN')}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    /* Single part (legacy) */
                    <div className="p-4 bg-gray-50 rounded-lg border">
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-gray-500">Part No:</span>
                          <span className="ml-2 font-medium">{viewRFQ.part_no}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Part Name:</span>
                          <span className="ml-2 font-medium">{viewRFQ.part_name}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Quantity:</span>
                          <span className="ml-2 font-medium">{viewRFQ.rfq_qty?.toLocaleString('en-IN')}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Category:</span>
                          <span className="ml-2">{Array.isArray(viewRFQ.category) ? viewRFQ.category.join(', ') : viewRFQ.category}</span>
                        </div>
                        {viewRFQ.part_description && (
                          <div className="col-span-2">
                            <span className="text-gray-500">Description:</span>
                            <span className="ml-2">{viewRFQ.part_description}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Pricing & Totals */}
                <div className="grid grid-cols-3 gap-4 pt-4 border-t">
                  <div>
                    <Label className="text-gray-600">Total Quantity</Label>
                    <p className="font-semibold text-lg">{viewRFQ.rfq_qty?.toLocaleString('en-IN') || viewRFQ.total_qty?.toLocaleString('en-IN')}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Quoted Price</Label>
                    <p className="font-medium">{viewRFQ.quoted_price ? `₹${parseFloat(viewRFQ.quoted_price).toLocaleString('en-IN')}` : '-'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Est. Business</Label>
                    <p className="font-bold text-green-700 text-lg">₹{viewRFQ.estimated_business?.toLocaleString('en-IN')}</p>
                  </div>
                </div>

                {/* PO Info */}
                <div className="grid grid-cols-3 gap-4 pt-4 border-t">
                  <div>
                    <Label className="text-gray-600">PO Number</Label>
                    <p className="font-medium">{viewRFQ.purchase_order_number || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">PO Qty</Label>
                    <p className="font-medium">{viewRFQ.po_qty?.toLocaleString('en-IN') || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">PO Value</Label>
                    <p className="font-medium">{viewRFQ.po_value ? `₹${parseFloat(viewRFQ.po_value).toLocaleString('en-IN')}` : '-'}</p>
                  </div>
                </div>

                {/* Files */}
                <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                  <div>
                    <Label className="text-gray-600">Drawing File</Label>
                    <p className="font-medium">{viewRFQ.drawing_file_name || 'No file'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Quotation File</Label>
                    <p className="font-medium">{viewRFQ.quotation_file_name || 'No file'}</p>
                  </div>
                </div>

                {/* Status */}
                <div className="pt-4 border-t">
                  <Label className="text-gray-600">Status</Label>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium mt-1 ${statusColors[viewRFQ.status || 'Pending']}`}>
                    {viewRFQ.status || 'Pending'}
                  </span>
                  {viewRFQ.status === 'RFQ Rejected' && (
                    <div className="mt-2 p-3 bg-red-50 rounded-lg border border-red-200">
                      <p className="text-sm text-red-800">
                        <span className="font-medium">Rejection Date:</span> {viewRFQ.rejection_date || '-'}
                      </p>
                      {viewRFQ.rejection_reason && (
                        <p className="text-sm text-red-800 mt-1">
                          <span className="font-medium">Reason:</span> {viewRFQ.rejection_reason}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <Button onClick={() => setViewRFQ(null)} className="bg-[#0A2540]">
                  Close
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Edit RFQ Modal */}
      {editRFQ && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-semibold text-[#0A2540]">Edit RFQ</h2>
                <button
                  onClick={() => setEditRFQ(null)}
                  className="text-gray-500 hover:text-gray-700"
                  data-testid="close-edit-modal"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Header Info */}
                <div className="grid grid-cols-2 gap-4 pb-4 border-b">
                  <div>
                    <Label className="text-gray-600">RFQ ID</Label>
                    <Input 
                      value={editFormData.rfq_id} 
                      onChange={(e) => setEditFormData({ ...editFormData, rfq_id: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label className="text-gray-600">Customer Code</Label>
                    <Input 
                      value={editFormData.customer_code} 
                      onChange={(e) => setEditFormData({ ...editFormData, customer_code: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label className="text-gray-600">Buyer Name</Label>
                    <Input
                      value={editFormData.buyer_name}
                      onChange={(e) => setEditFormData({ ...editFormData, buyer_name: e.target.value })}
                      placeholder="Enter buyer name"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-600">Status</Label>
                    <select
                      value={editFormData.status}
                      onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    >
                      {STATUS_OPTIONS.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Parts Section */}
                {editFormData.parts && editFormData.parts.length > 0 ? (
                  <div>
                    <Label className="text-gray-600 text-lg mb-3 block">
                      Parts ({editFormData.parts.length}) - Edit each part below
                    </Label>
                    <div className="space-y-4">
                      {editFormData.parts.map((part, idx) => (
                        <div key={idx} className="p-4 bg-gray-50 rounded-lg border">
                          <div className="font-semibold text-[#0A2540] mb-3">Part {idx + 1}</div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-gray-600 text-sm">Part No</Label>
                              <Input
                                value={part.part_no || ''}
                                onChange={(e) => {
                                  const newParts = [...editFormData.parts];
                                  newParts[idx] = { ...newParts[idx], part_no: e.target.value };
                                  setEditFormData({ ...editFormData, parts: newParts });
                                }}
                              />
                            </div>
                            <div>
                              <Label className="text-gray-600 text-sm">Part Name</Label>
                              <Input
                                value={part.part_name || ''}
                                onChange={(e) => {
                                  const newParts = [...editFormData.parts];
                                  newParts[idx] = { ...newParts[idx], part_name: e.target.value };
                                  setEditFormData({ ...editFormData, parts: newParts });
                                }}
                              />
                            </div>
                            <div>
                              <Label className="text-gray-600 text-sm">Quantity</Label>
                              <Input
                                type="number"
                                value={part.rfq_qty || ''}
                                onChange={(e) => {
                                  const newParts = [...editFormData.parts];
                                  newParts[idx] = { ...newParts[idx], rfq_qty: parseInt(e.target.value) || 0 };
                                  setEditFormData({ ...editFormData, parts: newParts });
                                }}
                              />
                            </div>
                            <div>
                              <Label className="text-gray-600 text-sm">Quoted Price (₹)</Label>
                              <Input
                                type="number"
                                step="0.01"
                                value={part.quoted_price || ''}
                                onChange={(e) => {
                                  const newParts = [...editFormData.parts];
                                  newParts[idx] = { ...newParts[idx], quoted_price: parseFloat(e.target.value) || 0 };
                                  setEditFormData({ ...editFormData, parts: newParts });
                                }}
                              />
                            </div>
                            <div className="col-span-2">
                              <Label className="text-gray-600 text-sm">Description</Label>
                              <Input
                                value={part.part_description || ''}
                                onChange={(e) => {
                                  const newParts = [...editFormData.parts];
                                  newParts[idx] = { ...newParts[idx], part_description: e.target.value };
                                  setEditFormData({ ...editFormData, parts: newParts });
                                }}
                              />
                            </div>
                            {/* Drawing file upload/replace option */}
                            <div className="col-span-2 mt-2">
                              <Label className="text-gray-600 text-sm">Drawing File</Label>
                              <div className="flex items-center gap-2 mt-1">
                                {part.drawing_file_name ? (
                                  <>
                                    <span className="text-sm text-blue-600 truncate max-w-[150px]">{part.drawing_file_name}</span>
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                      onClick={() => {
                                        const newParts = [...editFormData.parts];
                                        newParts[idx] = { ...newParts[idx], drawing_file_name: '', drawing_url: '' };
                                        setEditFormData({ ...editFormData, parts: newParts });
                                      }}
                                    >
                                      <Trash2 className="w-3 h-3 mr-1" /> Remove
                                    </Button>
                                  </>
                                ) : (
                                  <span className="text-sm text-gray-400">No file</span>
                                )}
                                <label className="cursor-pointer">
                                  <input
                                    type="file"
                                    className="hidden"
                                    accept=".pdf,.jpg,.jpeg,.png,.dwg,.dxf"
                                    onChange={async (e) => {
                                      const file = e.target.files?.[0];
                                      if (!file) return;
                                      const formData = new FormData();
                                      formData.append('file', file);
                                      try {
                                        const token = localStorage.getItem('auth_token');
                                        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload/drawing`, {
                                          method: 'POST',
                                          headers: { 'Authorization': `Bearer ${token}` },
                                          body: formData
                                        });
                                        if (!res.ok) throw new Error('Upload failed');
                                        const data = await res.json();
                                        const newParts = [...editFormData.parts];
                                        newParts[idx] = { ...newParts[idx], drawing_file_name: file.name, drawing_url: data.url };
                                        setEditFormData({ ...editFormData, parts: newParts });
                                        toast.success('Drawing uploaded');
                                      } catch (err) {
                                        toast.error('Failed to upload drawing');
                                      }
                                    }}
                                  />
                                  <span className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700">
                                    {part.drawing_file_name ? 'Replace' : 'Upload'}
                                  </span>
                                </label>
                              </div>
                            </div>
                            
                            {/* Per-part PO Fields */}
                            <div className="col-span-2 mt-3 pt-3 border-t border-gray-200">
                              <p className="text-sm font-medium text-gray-700 mb-2">Part-specific PO Details</p>
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                <div>
                                  <Label className="text-gray-600 text-xs">Quotation Date</Label>
                                  <Input
                                    type="date"
                                    value={part.quotation_date || ''}
                                    onChange={(e) => {
                                      const newParts = [...editFormData.parts];
                                      newParts[idx] = { ...newParts[idx], quotation_date: e.target.value };
                                      setEditFormData({ ...editFormData, parts: newParts });
                                    }}
                                    className="text-sm"
                                  />
                                </div>
                                <div>
                                  <Label className="text-gray-600 text-xs">Final Price (₹)</Label>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={part.final_price || ''}
                                    onChange={(e) => {
                                      const newParts = [...editFormData.parts];
                                      newParts[idx] = { ...newParts[idx], final_price: parseFloat(e.target.value) || 0 };
                                      setEditFormData({ ...editFormData, parts: newParts });
                                    }}
                                    className="text-sm"
                                  />
                                </div>
                                <div>
                                  <Label className="text-gray-600 text-xs">PO Number</Label>
                                  <Input
                                    value={part.po_number || ''}
                                    onChange={(e) => {
                                      const newParts = [...editFormData.parts];
                                      newParts[idx] = { ...newParts[idx], po_number: e.target.value };
                                      setEditFormData({ ...editFormData, parts: newParts });
                                    }}
                                    className="text-sm"
                                    placeholder="PO-XXXX"
                                  />
                                </div>
                                <div>
                                  <Label className="text-gray-600 text-xs">PO Qty</Label>
                                  <Input
                                    type="number"
                                    value={part.po_qty || ''}
                                    onChange={(e) => {
                                      const newParts = [...editFormData.parts];
                                      newParts[idx] = { ...newParts[idx], po_qty: parseInt(e.target.value) || 0 };
                                      setEditFormData({ ...editFormData, parts: newParts });
                                    }}
                                    className="text-sm"
                                  />
                                </div>
                                <div>
                                  <Label className="text-gray-600 text-xs">PO Value (₹)</Label>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={part.po_value || ''}
                                    onChange={(e) => {
                                      const newParts = [...editFormData.parts];
                                      newParts[idx] = { ...newParts[idx], po_value: parseFloat(e.target.value) || 0 };
                                      setEditFormData({ ...editFormData, parts: newParts });
                                    }}
                                    className="text-sm"
                                  />
                                </div>
                                <div>
                                  <Label className="text-gray-600 text-xs">Est. Business (₹)</Label>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={part.estimated_business || ''}
                                    onChange={(e) => {
                                      const newParts = [...editFormData.parts];
                                      newParts[idx] = { ...newParts[idx], estimated_business: parseFloat(e.target.value) || 0 };
                                      setEditFormData({ ...editFormData, parts: newParts });
                                    }}
                                    className="text-sm"
                                    placeholder="Projected value"
                                  />
                                </div>
                                <div>
                                  <Label className="text-gray-600 text-xs">Quotation File</Label>
                                  <div className="flex items-center gap-1">
                                    {part.quotation_file_name ? (
                                      <span className="text-xs text-blue-600 truncate max-w-[80px]">{part.quotation_file_name}</span>
                                    ) : null}
                                    <label className="cursor-pointer">
                                      <input
                                        type="file"
                                        className="hidden"
                                        accept=".pdf,.xlsx,.xls"
                                        onChange={async (e) => {
                                          const file = e.target.files?.[0];
                                          if (!file) return;
                                          const fd = new FormData();
                                          fd.append('file', file);
                                          try {
                                            const token = localStorage.getItem('auth_token');
                                            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload/quotation`, {
                                              method: 'POST',
                                              headers: { 'Authorization': `Bearer ${token}` },
                                              body: fd
                                            });
                                            if (!res.ok) throw new Error('Upload failed');
                                            const data = await res.json();
                                            const newParts = [...editFormData.parts];
                                            newParts[idx] = { ...newParts[idx], quotation_file_name: file.name, quotation_url: data.url };
                                            setEditFormData({ ...editFormData, parts: newParts });
                                            toast.success('Quotation uploaded');
                                          } catch (err) {
                                            toast.error('Failed to upload');
                                          }
                                        }}
                                      />
                                      <span className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700">
                                        {part.quotation_file_name ? 'Replace' : 'Upload'}
                                      </span>
                                    </label>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  /* Single part (legacy) */
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-gray-600">Part No. *</Label>
                        <Input
                          value={editFormData.part_no}
                          onChange={(e) => setEditFormData({ ...editFormData, part_no: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label className="text-gray-600">Part Name *</Label>
                        <Input
                          value={editFormData.part_name}
                          onChange={(e) => setEditFormData({ ...editFormData, part_name: e.target.value })}
                        />
                      </div>
                    </div>

                    <div>
                      <Label className="text-gray-600">Part Description *</Label>
                      <Textarea
                        value={editFormData.part_description}
                        onChange={(e) => setEditFormData({ ...editFormData, part_description: e.target.value })}
                        rows={2}
                      />
                    </div>

                    <div>
                      <Label className="text-gray-600">Category *</Label>
                      <div className="grid grid-cols-3 gap-2 mt-2">
                        {categoryOptions.map((cat) => (
                          <div key={cat} className="flex items-center space-x-2">
                            <Checkbox
                              id={`edit-${cat}`}
                              checked={editFormData.category?.includes(cat)}
                              onCheckedChange={() => handleCategoryToggle(cat)}
                            />
                            <label htmlFor={`edit-${cat}`} className="text-sm cursor-pointer">
                              {cat}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-gray-600">RFQ Quantity *</Label>
                        <Input
                          type="number"
                          value={editFormData.rfq_qty}
                          onChange={(e) => setEditFormData({ ...editFormData, rfq_qty: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label className="text-gray-600">Quoted Price (₹)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={editFormData.quoted_price}
                          onChange={(e) => setEditFormData({ ...editFormData, quoted_price: e.target.value })}
                        />
                      </div>
                    </div>
                  </>
                )}

                {/* Common Fields */}
                <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                  <div>
                    <Label className="text-gray-600">RFQ Date *</Label>
                    <Input
                      type="date"
                      value={editFormData.rfq_date}
                      onChange={(e) => setEditFormData({ ...editFormData, rfq_date: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label className="text-gray-600">Quotation Date</Label>
                    <Input
                      type="date"
                      value={editFormData.quotation_date}
                      onChange={(e) => setEditFormData({ ...editFormData, quotation_date: e.target.value })}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-600">Final Price (₹)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editFormData.final_price}
                      onChange={(e) => setEditFormData({ ...editFormData, final_price: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label className="text-gray-600">PO Number</Label>
                    <Input
                      value={editFormData.purchase_order_number}
                      onChange={(e) => setEditFormData({ ...editFormData, purchase_order_number: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label className="text-gray-600">PO Qty</Label>
                    <Input
                      type="number"
                      value={editFormData.po_qty}
                      onChange={(e) => setEditFormData({ ...editFormData, po_qty: e.target.value })}
                      placeholder="Enter PO quantity"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-600">PO Order Value (₹)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editFormData.po_value}
                      onChange={(e) => setEditFormData({ ...editFormData, po_value: e.target.value })}
                      placeholder="Enter PO value"
                    />
                  </div>
                </div>

                {/* Quotation File Upload/Replace */}
                <div className="pt-4 border-t">
                  <Label className="text-gray-600">Quotation File</Label>
                  <div className="flex items-center gap-2 mt-2">
                    {editFormData.quotation_file_name ? (
                      <>
                        <span className="text-sm text-blue-600 truncate max-w-[200px]">{editFormData.quotation_file_name}</span>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => {
                            setEditFormData({ ...editFormData, quotation_file_name: '', quotation_url: '' });
                          }}
                        >
                          <Trash2 className="w-3 h-3 mr-1" /> Remove
                        </Button>
                      </>
                    ) : (
                      <span className="text-sm text-gray-400">No quotation file</span>
                    )}
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        className="hidden"
                        accept=".pdf,.xlsx,.xls,.docx"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const formData = new FormData();
                          formData.append('file', file);
                          try {
                            const token = localStorage.getItem('auth_token');
                            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload/quotation`, {
                              method: 'POST',
                              headers: { 'Authorization': `Bearer ${token}` },
                              body: formData
                            });
                            if (!res.ok) throw new Error('Upload failed');
                            const data = await res.json();
                            setEditFormData({ ...editFormData, quotation_file_name: file.name, quotation_url: data.url });
                            toast.success('Quotation file uploaded');
                          } catch (err) {
                            toast.error('Failed to upload quotation');
                          }
                        }}
                      />
                      <span className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700">
                        {editFormData.quotation_file_name ? 'Replace' : 'Upload'}
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <Button variant="outline" onClick={() => setEditRFQ(null)}>
                  Cancel
                </Button>
                <Button onClick={handleEditSave} className="bg-[#0A2540]" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* File Preview Modal - With Actual Preview */}
      {previewFile && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-4xl w-full max-h-[90vh] bg-white rounded-lg shadow-xl overflow-hidden flex flex-col">
            <div className="p-4 border-b flex items-center justify-between bg-white shrink-0">
              <h2 className="text-lg font-semibold text-[#0A2540] truncate">{previewFile.name}</h2>
              <button
                onClick={() => setPreviewFile(null)}
                className="text-gray-500 hover:text-gray-700"
                data-testid="close-preview-modal"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-1 overflow-auto bg-gray-100 p-4 min-h-[300px]">
              {previewFile.url ? (
                (() => {
                  const fullUrl = previewFile.url.startsWith('http') ? previewFile.url : `${process.env.REACT_APP_BACKEND_URL}${previewFile.url}`;
                  const fileName = previewFile.name.toLowerCase();
                  const isImage = fileName.endsWith('.jpg') || fileName.endsWith('.jpeg') || fileName.endsWith('.png');
                  const isPdf = fileName.endsWith('.pdf');
                  
                  if (isImage) {
                    return (
                      <div className="flex items-center justify-center h-full">
                        <img 
                          src={fullUrl} 
                          alt={previewFile.name}
                          className="max-w-full max-h-[60vh] object-contain rounded shadow-lg"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = '<div class="text-center text-gray-500"><p>Failed to load image</p></div>';
                          }}
                        />
                      </div>
                    );
                  } else if (isPdf) {
                    return (
                      <div className="h-full min-h-[500px]">
                        <iframe
                          src={fullUrl}
                          className="w-full h-full min-h-[500px] border-0 rounded shadow-lg bg-white"
                          title={previewFile.name}
                        />
                      </div>
                    );
                  } else {
                    // For other files (Excel, DWG, etc.) - show icon and download option
                    return (
                      <div className="flex flex-col items-center justify-center h-full py-12">
                        <File className={`w-20 h-20 mb-4 ${isExcelFile(previewFile.name) ? 'text-green-600' : 'text-blue-600'}`} />
                        <p className="font-medium text-gray-800 mb-2">{previewFile.name}</p>
                        <p className="text-sm text-gray-500 mb-2">
                          {isExcelFile(previewFile.name) ? 'Excel File' : 'File'} - Preview not available
                        </p>
                        <p className="text-xs text-gray-400 mb-6">Click download to view this file</p>
                      </div>
                    );
                  }
                })()
              ) : (
                <div className="flex flex-col items-center justify-center h-full py-12">
                  <File className="w-16 h-16 text-gray-400 mb-2" />
                  <p className="text-gray-500">File not available</p>
                </div>
              )}
            </div>

            <div className="p-4 border-t bg-white flex justify-between items-center shrink-0">
              <div className="text-sm text-gray-500">
                {isPdfFile(previewFile.name) ? 'PDF Document' : isExcelFile(previewFile.name) ? 'Excel Spreadsheet' : previewFile.name.toLowerCase().endsWith('.png') || previewFile.name.toLowerCase().endsWith('.jpg') || previewFile.name.toLowerCase().endsWith('.jpeg') ? 'Image File' : 'Document'}
              </div>
              <div className="flex gap-2">
                {previewFile.url && (
                  <button
                    onClick={() => {
                      const fullUrl = previewFile.url.startsWith('http') ? previewFile.url : `${process.env.REACT_APP_BACKEND_URL}${previewFile.url}`;
                      const link = document.createElement('a');
                      link.href = fullUrl;
                      link.download = previewFile.name;
                      link.click();
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </button>
                )}
                <button
                  onClick={() => setPreviewFile(null)}
                  className="px-4 py-2 bg-[#0A2540] text-white rounded-lg hover:bg-[#0A2540]/90 text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Deny RFQ Modal */}
      {denyModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full bg-white rounded-lg shadow-xl">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-[#0A2540] mb-4">Deny RFQ</h2>
              <p className="text-gray-600 mb-4">
                Are you sure you want to deny RFQ <strong>{denyModal.rfqName}</strong>? 
                It will remain in history but cannot be converted to orders.
              </p>
              <div className="mb-4">
                <Label className="text-gray-600">Reason for Denial (optional)</Label>
                <Textarea
                  value={denyReason}
                  onChange={(e) => setDenyReason(e.target.value)}
                  placeholder="Enter reason for denying this RFQ..."
                  rows={3}
                  className="mt-1"
                />
              </div>
              <div className="flex justify-end gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => setDenyModal({ open: false, rfqId: null, rfqName: '' })}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleDenyRFQ}
                  className="bg-gray-600 hover:bg-gray-700"
                >
                  Deny RFQ
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </Layout>
  );
}
